import { ColorInput } from '@mantine/core';
import c from 'classnames';
import arrayMutators from 'final-form-arrays';
import { curry } from 'lodash/fp';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useToasts } from 'react-toast-notifications';
import Toggle from '../../../common/Toggle';
import AdvancedSettingsToggle from '../../../components/AdvancedSettingsToggle';
import BlockNavigation from '../../../components/BlockNavigation';
import Button from '../../../components/Button';
import CodeEditor from '../../../components/CodeEditor';
import Error from '../../../components/Error';
import { FormSection, FormSectionBody, FormSectionHeader, StyledForm } from '../../../components/Form';
import AutoSave from '../../../components/FormAutoSave';
import { Pill } from '../../../components/Pill';
import { CurrentCompanyContext } from '../../../hooks/CurrentCompanyContext';
import { ROUTES } from '../../../routes/routes';
import CustomData from '../../../types/customData';
import {
    CANCELLATION_MODE,
    CANCELLATION_PRORATION_BEHAVIOUR,
    CANCELLATION_REASON_COLLECTION_MODE,
    CANCELLATION_REASON_OPTIONS,
    CancellationReason,
    CustomerPortal,
    CustomerPortalButtonSize,
    CustomerPortalLayout,
    CustomerUpdate,
    DisabledProduct,
    SUBSCRIPTION_UPDATE_PRORATION,
    SubscriptionUpdate,
} from '../../../types/customerPortal';
import { PricingPage } from '../../../types/pricingPage';
import { StripePrice } from '../../../types/stripePrice';
import { DEFAULT_PORTAL_CUSTOM_CSS } from '../../../utils/constants';
import { formatFrequency, formatPrice } from '../../../utils/currency';
import { useAxios } from '../../../utils/hooks';
import { isStripePrice } from '../../../utils/products';
import { toFriendlyName } from '../../../utils/strings';
import { enumKeys } from '../../../utils/typescript';
import VALIDATORS from '../../../utils/validators';
import Footer from '../../PricingPages/NewPage/Footer';
import CustomDataModal from './CustomDataModal';
import VerifySenderModal from './VerifySenderModal';

const BUTTON_SIZE_OPTIONS = enumKeys(CustomerPortalButtonSize).map((key) => CustomerPortalButtonSize[key]);
const LAYOUT_OPTIONS = enumKeys(CustomerPortalLayout).map((key) => CustomerPortalLayout[key]);
const CANCELLATION_REASON_MODE_OPTIONS = enumKeys(CANCELLATION_REASON_COLLECTION_MODE).map(
    (key) => CANCELLATION_REASON_COLLECTION_MODE[key]
);

const CUSTOMER_UPDATE_OPTIONS: (keyof Required<CustomerUpdate>['allowed_updates'])[] = [
    'email',
    'address',
    'shipping',
    'phone',
    'tax_id',
];

const SUBSCRIPTION_UPDATE_OPTIONS: (keyof SubscriptionUpdate['default_allowed_updates'])[] = [
    'price',
    'promotion_code',
    'quantity',
];

const toOptionName = (type: CANCELLATION_MODE | CANCELLATION_PRORATION_BEHAVIOUR | SUBSCRIPTION_UPDATE_PRORATION) => {
    switch (type) {
        case CANCELLATION_MODE.PERIOD_END:
            return 'At the end of the billing period';
        case CANCELLATION_MODE.IMMEDIATELY:
            return 'Immediately';
        case CANCELLATION_PRORATION_BEHAVIOUR.NONE:
            return 'No proration';
        case CANCELLATION_PRORATION_BEHAVIOUR.CREATE_PRORATIONS:
            return 'Prorate remaining period';
        case SUBSCRIPTION_UPDATE_PRORATION.ALWAYS_INVOICE:
            return 'Create an invoice';
        case SUBSCRIPTION_UPDATE_PRORATION.NONE:
            return 'Do nothing';
        case SUBSCRIPTION_UPDATE_PRORATION.PRORATION:
            return 'Prorate remaining period';
        default:
            return type;
    }
};

const matchesOption = curry(
    (option: StripePrice, { product, price }: DisabledProduct) => product === option.product.id && price === option.id
);

interface Props {
    customerPortal: CustomerPortal;
    page?: PricingPage;
    onUpdate: (customerPortal: CustomerPortal) => Promise<void>;
}

export default function Configure({ onUpdate, customerPortal, page }: Props) {
    const axios = useAxios();
    const { addToast } = useToasts();
    const company = useContext(CurrentCompanyContext);
    const [showCustomCss, setShowCustomerCss] = useState(false);
    const handleToggleCustomCss = () => setShowCustomerCss(!showCustomCss);
    const [showAdvanced, setShowAdvanced] = useState(customerPortal.secureMode || customerPortal.emailAuth);
    const handleToggleShowAdvanced = () => setShowAdvanced(!showAdvanced);
    const [showCustomWording, setShowCustomWording] = useState(false);
    const handleShowCustomWording = () => setShowCustomWording(true);
    const [customData, setCustomData] = useState<CustomData[]>([]);
    const [showCustomData, setShowCustomData] = useState(false);
    const handleShowCustomData = () => setShowCustomData(true);
    const handleHideCustomData = () => setShowCustomData(false);
    const loadCustomData = useCallback(() => {
        if (!axios.current) {
            return;
        }

        axios.current
            .get<CustomData[]>('/custom-data')
            .then((res) => setCustomData(res.data))
            .catch((err) => console.error('Error fetching custom data'));
    }, [axios]);
    const handleNewCustomData = (newData: CustomData) => {
        setShowCustomData(false);
        loadCustomData();
    };
    const handleDeleteCustomData = ({ id }: CustomData) => {
        axios.current
            ?.delete(`/custom-data/${id}`)
            .then(loadCustomData)
            .catch((err) =>
                addToast('Something went wrong. Please reload the page and try again.', { appearance: 'error' })
            );
    };

    useEffect(loadCustomData, [loadCustomData]);
    // FormAutoSave means we don't use the form submission process so store errors manually here
    const [submitError, setSubmitError] = useState<string>();

    const handleSave = async (values: CustomerPortal) => {
        setSubmitError(undefined);
        try {
            await onUpdate(values);
        } catch (err) {
            setSubmitError('Error Saving.');
        }
    };
    const isStripeConnected = !!company?.stripe?.stripeId;

    const ready = useMemo(
        () =>
            customerPortal.designSettings.buttonText &&
            customerPortal.business_profile.privacy_policy_url &&
            customerPortal.business_profile.terms_of_service_url &&
            isStripeConnected,
        [customerPortal, isStripeConnected]
    );

    const stripeProducts: StripePrice[] = (page?.plans ?? [])
        .filter((plan) => isStripePrice(plan.product))
        .map((plan) => plan.product as StripePrice);

    return (
        <div>
            <div className="font-bold mb-4">Customize your Customer Portal</div>
            <Form<CustomerPortal>
                onSubmit={handleSave}
                initialValues={{ ...customerPortal }}
                mutators={{
                    clearCustomerUpdates: (_args, state, utils) => {
                        utils.changeValue(state, 'features.customer_update.allowed_updates', () => undefined);
                    },
                    clearCancellationUpdates: (_args, state, utils) => {
                        utils.changeValue(state, 'features.subscription_cancel.mode', () => undefined);
                        utils.changeValue(state, 'features.subscription_cancel.proration_behavior', () => undefined);
                    },
                    clearCancellationProration: (_args, state, utils) => {
                        utils.changeValue(state, 'features.subscription_cancel.proration_behavior', () => undefined);
                    },
                    clearSubscriptionUpdates: (_args, state, utils) => {
                        utils.changeValue(
                            state,
                            'features.subscription_update.default_allowed_updates',
                            () => undefined
                        );
                        utils.changeValue(state, 'features.subscription_update.proration_behavior', () => undefined);
                    },
                    disableSubscriptionUpdates: (_args, state, utils) => {
                        utils.changeValue(state, 'features.subscription_update', () => ({
                            enabled: false,
                            default_allowed_updates: undefined,
                        }));
                    },
                    setDefaultSubscriptionUpdates: (_args, state, utils) => {
                        utils.changeValue(state, 'features.subscription_update.default_allowed_updates', () => ({
                            price: true,
                        }));
                    },
                    ...arrayMutators,
                }}
            >
                {({ handleSubmit, values, form }) => (
                    <StyledForm onSubmit={handleSubmit}>
                        <FormSection>
                            <FormSectionHeader>Your Business</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Company Name</label>
                                    <Field name="business_profile.company" component="input" type="text">
                                        {({ input, meta }) => (
                                            <>
                                                <input {...input} placeholder="Your Company" className="input w-full" />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">
                                        Headline (
                                        <button onClick={handleShowCustomData} className="link">
                                            Manage Custom Data
                                        </button>
                                        )
                                    </label>
                                    {customData && (
                                        <div className="ml-4 py-2">
                                            <div className="text-sm">
                                                Custom Data:
                                                {customData.map((data) => (
                                                    <Button
                                                        key={data.id}
                                                        kind="outline"
                                                        small
                                                        className="ml-2 mb-2"
                                                        onClick={() =>
                                                            form.change(
                                                                'business_profile.headline' as any, // typings don't allow for dot notation atm
                                                                (values.business_profile.headline || '') +
                                                                    `{{${data.name}}}`
                                                            )
                                                        }
                                                    >{`{{${data.name}}}`}</Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <Field
                                        name="business_profile.headline"
                                        type="text"
                                        validate={VALIDATORS.maxLength(60)}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    className="input w-full"
                                                    placeholder="The messaging shown to customers in the portal."
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">
                                        Privacy Policy link<b>*</b>
                                    </label>
                                    <Field
                                        name="business_profile.privacy_policy_url"
                                        component="input"
                                        type="url"
                                        validate={VALIDATORS.requiredUrl}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="e.g. https://YOUR-PUBLIC-SITE/privacy"
                                                    className="input w-full"
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">
                                        Terms of Service link<b>*</b>
                                    </label>
                                    <Field
                                        name="business_profile.terms_of_service_url"
                                        component="input"
                                        type="url"
                                        validate={VALIDATORS.requiredUrl}
                                    >
                                        {({ input, meta }) => (
                                            <>
                                                <input
                                                    {...input}
                                                    placeholder="e.g. https://YOUR-PUBLIC-SITE/terms"
                                                    className="input w-full"
                                                />
                                                {meta.error && (
                                                    <span className="ml-2">
                                                        <Error message={meta.error} />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Field>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Design</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Design color</label>
                                    <Field name="designSettings.buttonColour">
                                        {(props) => (
                                            <ColorInput
                                                {...props.input}
                                                size="md"
                                                format="hex"
                                                withPicker={false}
                                                swatches={[
                                                    '#2e2e2e',
                                                    '#fa5252',
                                                    '#e64980',
                                                    '#be4bdb',
                                                    '#7950f2',
                                                    '#4c6ef5',
                                                    '#228be6',
                                                    '#15aabf',
                                                ]}
                                            />
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <label className="text-sm text-gray-600">Button size</label>
                                    <Field name="designSettings.buttonSize" component="select">
                                        {(props) => (
                                            <select {...props.input} className="block pl-3 pr-10 select">
                                                {BUTTON_SIZE_OPTIONS.map((option) => (
                                                    <option value={option} key={option}>
                                                        {toFriendlyName(option)}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </Field>
                                </div>
                                <div className="mb-2">
                                    <div className="text-sm text-gray-600 mb-2">Layout</div>
                                    <div className="flex space-x-4">
                                        {LAYOUT_OPTIONS.map((option) => (
                                            <Field
                                                name="designSettings.layout"
                                                component="input"
                                                type="radio"
                                                value={option}
                                                key={option}
                                            >
                                                {(props) => (
                                                    <label>
                                                        <input {...props.input} className="form-radio mr-2" />
                                                        <span className="form-check-label inline-block text-gray-800">
                                                            {toFriendlyName(option)}
                                                        </span>
                                                    </label>
                                                )}
                                            </Field>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label>
                                        Custom CSS{' '}
                                        <button className="text-xs ml-2 underline" onClick={handleToggleCustomCss}>
                                            {showCustomCss ? 'Hide' : 'Show'} Custom CSS
                                        </button>
                                    </label>
                                    <Field name="designSettings.customCSS" component="textarea" type="text">
                                        {({ input: { onChange, ...inputProps } }) => (
                                            <div className={c({ hidden: !showCustomCss })}>
                                                <CodeEditor
                                                    onChange={(code) =>
                                                        onChange(!code ? DEFAULT_PORTAL_CUSTOM_CSS : code)
                                                    }
                                                    {...inputProps}
                                                    language="css"
                                                />
                                            </div>
                                        )}
                                    </Field>
                                </div>
                                <div className="pt-4 text-sm flex">
                                    <Pill title="Tip" colour="PURPLE" />
                                    <div className="ml-2">
                                        <div className="font-bold text-center">
                                            Want to use your own HTML/JS for the button?
                                        </div>
                                        <div>Skip these design settings and see the instructions on the next page.</div>
                                    </div>
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <hr className="border-t" />
                        <FormSection>
                            <FormSectionHeader>Payment Method</FormSectionHeader>
                            <FormSectionBody>
                                <Field name="features.payment_method_update.enabled" type="checkbox">
                                    {({ input: { onChange, ...inputProps } }) => (
                                        <label className="inline-flex items-center">
                                            <Toggle
                                                onChange={(value) => {
                                                    onChange(value);
                                                    if (!value) {
                                                        form.mutators.disableSubscriptionUpdates();
                                                    }
                                                }}
                                                {...inputProps}
                                            />
                                            <span className="ml-2 text-sm text-gray-600">
                                                Can customers update their payment method?
                                            </span>
                                        </label>
                                    )}
                                </Field>
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Update Subscription</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <Field name="features.subscription_update.enabled" type="checkbox">
                                        {({ input: { onChange, ...inputProps } }) => (
                                            <label className="inline-flex items-center">
                                                <Toggle
                                                    disabled={!values.features.payment_method_update.enabled}
                                                    onChange={(value) => {
                                                        onChange(value);
                                                        if (!value) {
                                                            form.mutators.clearSubscriptionUpdates();
                                                        } else {
                                                            form.mutators.setDefaultSubscriptionUpdates();
                                                        }
                                                    }}
                                                    {...inputProps}
                                                />
                                                <span className="ml-2 text-sm text-gray-600">
                                                    Can customers update their subscription?
                                                </span>
                                            </label>
                                        )}
                                    </Field>
                                </div>
                                {values.features.subscription_update?.enabled && (
                                    <div className="pl-14">
                                        <div className="mb-4">
                                            <span className="text-sm text-gray-600 mb-2">
                                                Which of the following can the customer update?
                                            </span>
                                            {SUBSCRIPTION_UPDATE_OPTIONS.map((name) => (
                                                <div key={name}>
                                                    <Field
                                                        name={`features.subscription_update.default_allowed_updates.${name}`}
                                                        type="checkbox"
                                                    >
                                                        {(props) => (
                                                            <label className="inline-flex items-center">
                                                                <input
                                                                    {...props.input}
                                                                    type="checkbox"
                                                                    className="checkbox"
                                                                    defaultValue="false"
                                                                />
                                                                <span className="ml-2 text-gray-700">
                                                                    {name === 'price' ? 'Plan' : toFriendlyName(name)}
                                                                </span>
                                                            </label>
                                                        )}
                                                    </Field>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mb-4">
                                            <label className="text-sm text-gray-600">Proration behaviour</label>
                                            <Field
                                                name="features.subscription_update.proration_behavior"
                                                component="select"
                                            >
                                                {(props) => (
                                                    <select {...props.input} className="select">
                                                        {enumKeys(SUBSCRIPTION_UPDATE_PRORATION).map((key) => (
                                                            <option
                                                                key={key}
                                                                value={SUBSCRIPTION_UPDATE_PRORATION[key]}
                                                            >
                                                                {toOptionName(SUBSCRIPTION_UPDATE_PRORATION[key])}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </Field>
                                        </div>
                                        <div className="mb-4 flex flex-col">
                                            <FieldArray name="features.subscription_update.disabled_products">
                                                {({ fields }) => {
                                                    const handleToggle = (
                                                        enabled: boolean,
                                                        disabledProduct: DisabledProduct
                                                    ) => {
                                                        if (!enabled) {
                                                            fields.push(disabledProduct);
                                                        } else {
                                                            fields.remove(
                                                                fields.value.findIndex(
                                                                    ({ product, price }: DisabledProduct) =>
                                                                        product === disabledProduct.product &&
                                                                        price === disabledProduct.price
                                                                )
                                                            );
                                                        }
                                                    };
                                                    const handleReset = () =>
                                                        fields.value.splice(0, fields.value.length);
                                                    return (
                                                        <>
                                                            <label className="mb-2">
                                                                Customers can switch to: (
                                                                <button
                                                                    className="link underline"
                                                                    onClick={handleReset}
                                                                >
                                                                    reset
                                                                </button>
                                                                )
                                                            </label>
                                                            {stripeProducts.map((option) => (
                                                                <label
                                                                    className="inline-flex items-center mb-1"
                                                                    key={option.id}
                                                                >
                                                                    <Toggle
                                                                        onChange={(value) =>
                                                                            handleToggle(value, {
                                                                                product: option.product.id,
                                                                                price: option.id,
                                                                            })
                                                                        }
                                                                        name={option.product.name}
                                                                        checked={
                                                                            (fields.value || []).findIndex(
                                                                                matchesOption(option)
                                                                            ) === -1
                                                                        }
                                                                    />
                                                                    <span className="text-gray-700 ml-2">
                                                                        {option.product.name} (
                                                                        {formatPrice(option) + formatFrequency(option)})
                                                                    </span>
                                                                </label>
                                                            ))}
                                                        </>
                                                    );
                                                }}
                                            </FieldArray>
                                        </div>
                                    </div>
                                )}
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Cancel Subscription</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <Field name="features.subscription_cancel.enabled" type="checkbox">
                                        {({ input: { onChange, ...inputProps } }) => (
                                            <label className="inline-flex items-center">
                                                <Toggle
                                                    onChange={(value) => {
                                                        onChange(value);
                                                        if (!value) {
                                                            form.mutators.clearCancellationUpdates();
                                                            form.change(
                                                                'features.subscription_cancel.mode' as any,
                                                                CANCELLATION_MODE.PERIOD_END
                                                            );
                                                        }
                                                    }}
                                                    {...inputProps}
                                                />
                                                <span className="ml-2 text-sm text-gray-600">
                                                    Can customers cancel their subscription?
                                                </span>
                                            </label>
                                        )}
                                    </Field>
                                </div>
                                {values.features.subscription_cancel?.enabled && (
                                    <div className="pl-14">
                                        <div className="mb-2">
                                            <label className="text-sm text-gray-600">
                                                Subscription will be cancelled
                                            </label>
                                            <Field name="features.subscription_cancel.mode" component="select">
                                                {({ input: { onChange, ...inputProps } }) => (
                                                    <select
                                                        onChange={(value) => {
                                                            onChange(value);
                                                            if (
                                                                value.currentTarget.value !==
                                                                CANCELLATION_MODE.IMMEDIATELY
                                                            ) {
                                                                form.mutators.clearCancellationProration();
                                                            }
                                                        }}
                                                        {...inputProps}
                                                        className="select"
                                                    >
                                                        {enumKeys(CANCELLATION_MODE).map((key) => (
                                                            <option key={key} value={CANCELLATION_MODE[key]}>
                                                                {toOptionName(CANCELLATION_MODE[key])}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </Field>
                                        </div>
                                        {values.features.subscription_cancel?.mode ===
                                            CANCELLATION_MODE.IMMEDIATELY && (
                                            <div className="mb-2">
                                                <label className="text-sm text-gray-600">
                                                    Prorate upon cancellation?
                                                </label>
                                                <Field
                                                    name="features.subscription_cancel.proration_behavior"
                                                    component="select"
                                                >
                                                    {(props) => (
                                                        <select {...props.input} className="select">
                                                            {enumKeys(CANCELLATION_PRORATION_BEHAVIOUR).map((key) => (
                                                                <option
                                                                    key={key}
                                                                    value={CANCELLATION_PRORATION_BEHAVIOUR[key]}
                                                                >
                                                                    {toOptionName(
                                                                        CANCELLATION_PRORATION_BEHAVIOUR[key]
                                                                    )}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </Field>
                                            </div>
                                        )}
                                        <div className="mt-2">
                                            <Field
                                                name="features.subscription_cancel.cancellation_reason.enabled"
                                                type="checkbox"
                                            >
                                                {(props) => (
                                                    <label className="inline-flex items-center">
                                                        <Toggle {...props.input} />
                                                        <span className="ml-2 text-sm text-gray-600">
                                                            Collect cancellation reason?
                                                        </span>
                                                    </label>
                                                )}
                                            </Field>
                                        </div>
                                        {values.features.subscription_cancel?.cancellation_reason?.enabled && (
                                            <>
                                                <div className="mb-4 flex flex-col">
                                                    <label className="mb-2">Cancellation Reasons</label>
                                                    <FieldArray
                                                        name="features.subscription_cancel.cancellation_reason.options"
                                                        validate={VALIDATORS.checkedMin(2)}
                                                    >
                                                        {({ fields, meta }) => {
                                                            const toggle = (
                                                                event: React.ChangeEvent<HTMLInputElement>,
                                                                option: CancellationReason
                                                            ) => {
                                                                if (event.target.checked) {
                                                                    fields.push(option);
                                                                } else {
                                                                    fields.remove(
                                                                        fields.value.findIndex((o) => o === option)
                                                                    );
                                                                }
                                                            };

                                                            return (
                                                                <>
                                                                    {CANCELLATION_REASON_OPTIONS.map((option) => (
                                                                        <label
                                                                            className={'inline-flex items-center'}
                                                                            key={option}
                                                                        >
                                                                            <input
                                                                                onChange={(e) => toggle(e, option)}
                                                                                type="checkbox"
                                                                                className="checkbox"
                                                                                value={option}
                                                                                checked={
                                                                                    (fields.value || []).includes(
                                                                                        option
                                                                                    ) || false
                                                                                }
                                                                            />
                                                                            <span className="text-gray-700 ml-2">
                                                                                {toFriendlyName(option)}
                                                                            </span>
                                                                        </label>
                                                                    ))}
                                                                    {meta.error && (
                                                                        <span className="ml-2">
                                                                            <Error message={meta.error} />
                                                                        </span>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </FieldArray>
                                                </div>
                                                <div className="mb-4">
                                                    <div className="text-sm text-gray-600 mb-2">
                                                        When to collect the reason?
                                                    </div>
                                                    <div className="flex space-x-4">
                                                        {CANCELLATION_REASON_MODE_OPTIONS.map((option) => (
                                                            <Field
                                                                name="features.subscription_cancel.cancellation_reason.mode"
                                                                component="input"
                                                                type="radio"
                                                                value={option}
                                                                key={option}
                                                            >
                                                                {(props) => (
                                                                    <label>
                                                                        <input
                                                                            {...props.input}
                                                                            className="form-radio mr-2"
                                                                        />
                                                                        <span className="form-check-label inline-block text-gray-800">
                                                                            {toFriendlyName(option)} Cancelling
                                                                        </span>
                                                                    </label>
                                                                )}
                                                            </Field>
                                                        ))}
                                                    </div>
                                                </div>
                                                {values.features.subscription_cancel?.cancellation_reason?.mode ===
                                                    CANCELLATION_REASON_COLLECTION_MODE.BEFORE && (
                                                    <div className="mb-2">
                                                        <Field
                                                            name="features.subscription_cancel.cancellation_reason.refresh"
                                                            type="checkbox"
                                                        >
                                                            {(props) => (
                                                                <label className="inline-flex items-center">
                                                                    <Toggle {...props.input} />
                                                                    <span className="ml-2 text-sm text-gray-600">
                                                                        Refresh page after cancellation
                                                                    </span>
                                                                </label>
                                                            )}
                                                        </Field>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </FormSectionBody>
                        </FormSection>
                        {/* <FormSection>
                            <FormSectionHeader>Subscription Pause</FormSectionHeader>
                            <FormSectionBody>
                                <Field name="features.subscription_pause.enabled" type="checkbox">
                                    {(props) => (
                                        <label className="inline-flex items-center">
                                            <Toggle {...props.input} />
                                            <span className="ml-2 text-sm text-gray-600">
                                                Can customers pause their subscription?
                                            </span>
                                        </label>
                                    )}
                                </Field>
                            </FormSectionBody>
                        </FormSection> */}
                        <FormSection>
                            <FormSectionHeader>Customer Update</FormSectionHeader>
                            <FormSectionBody>
                                <div className="mb-2">
                                    <Field name="features.customer_update.enabled" type="checkbox">
                                        {({ input: { onChange, ...inputProps } }) => (
                                            <label className="inline-flex items-center">
                                                <Toggle
                                                    onChange={(value) => {
                                                        onChange(value);
                                                        if (!value) {
                                                            form.mutators.clearCustomerUpdates();
                                                        }
                                                    }}
                                                    {...inputProps}
                                                />
                                                <span className="ml-2 text-sm text-gray-600">
                                                    Customers can update their details?
                                                </span>
                                            </label>
                                        )}
                                    </Field>
                                </div>
                                {values.features.customer_update?.enabled && (
                                    <div className="pl-14">
                                        <span className="text-sm text-gray-600 mb-2">
                                            Which of the following can the customer update?
                                        </span>
                                        {CUSTOMER_UPDATE_OPTIONS.map((name) => (
                                            <div key={name}>
                                                <Field
                                                    name={`features.customer_update.allowed_updates.${name}`}
                                                    type="checkbox"
                                                >
                                                    {(props) => (
                                                        <label className="inline-flex items-center">
                                                            <input
                                                                {...props.input}
                                                                type="checkbox"
                                                                className="checkbox"
                                                                defaultValue="false"
                                                            />
                                                            <span className="ml-2 text-gray-700">
                                                                {toFriendlyName(name)}
                                                            </span>
                                                        </label>
                                                    )}
                                                </Field>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </FormSectionBody>
                        </FormSection>
                        <FormSection>
                            <FormSectionHeader>Invoice History</FormSectionHeader>
                            <FormSectionBody>
                                <Field name="features.invoice_history.enabled" type="checkbox">
                                    {(props) => (
                                        <label className="inline-flex items-center">
                                            <Toggle {...props.input} />
                                            <span className="ml-2 text-sm text-gray-600">
                                                Show customers their billing history?
                                            </span>
                                        </label>
                                    )}
                                </Field>
                            </FormSectionBody>
                        </FormSection>
                        <hr className="border-t" />
                        <FormSection>
                            <FormSectionHeader>Custom Wording</FormSectionHeader>
                            <FormSectionBody>
                                <AdvancedSettingsToggle
                                    title="Custom Wording"
                                    value={showCustomWording}
                                    onChange={handleShowCustomWording}
                                />
                                <div className={c({ hidden: !showCustomWording })}>
                                    <div className="mb-2">
                                        <label className="text-sm text-gray-600">Manage Subscription</label>
                                        <Field
                                            name="designSettings.buttonText"
                                            type="text"
                                            validate={VALIDATORS.required}
                                        >
                                            {(props) => (
                                                <>
                                                    <input {...props.input} className="input w-full" />
                                                    {props.meta.error && (
                                                        <span className="ml-2">
                                                            <Error message={props.meta.error} />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Field>
                                    </div>
                                    {values.features.subscription_cancel.cancellation_reason.mode ===
                                        CANCELLATION_REASON_COLLECTION_MODE.BEFORE && (
                                        <div className="mb-2">
                                            <label className="text-sm text-gray-600">Cancel Subscription</label>
                                            <Field
                                                name="designSettings.customWording.cancelSubscription"
                                                component="input"
                                            >
                                                {({ input, meta }) => (
                                                    <>
                                                        <input
                                                            {...input}
                                                            placeholder="Cancel Subscription"
                                                            className="input w-full"
                                                        />
                                                        {meta.error && (
                                                            <span className="ml-2">
                                                                <Error message={meta.error} />
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                    )}
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <hr className="border-t" />
                        <FormSection>
                            <FormSectionHeader>Advanced</FormSectionHeader>
                            <FormSectionBody>
                                <AdvancedSettingsToggle value={showAdvanced} onChange={handleToggleShowAdvanced} />
                                <div className={c({ hidden: !showAdvanced })}>
                                    <AdvancedSettings values={values} />
                                </div>
                            </FormSectionBody>
                        </FormSection>
                        <div className="flex px-8">
                            <div className="mr-0 ml-auto">
                                {submitError && <Error message={submitError} />}
                                <AutoSave debounce={1000} save={handleSave} />
                            </div>
                        </div>
                        {!isStripeConnected && (
                            <div className="text-center font-bold pb-8">
                                <Pill colour="YELLOW" title="Stripe not connected" /> Look up your real customers in
                                Stripe by pressing "Connect Stripe" above.
                            </div>
                        )}
                    </StyledForm>
                )}
            </Form>
            <Footer
                next={{
                    href: ROUTES.CUSTOMER_PORTALS_SNIPPET,
                    disabled: !ready,
                }}
                prev={{
                    href: ROUTES.CUSTOMER_PORTALS_NEW,
                    disabled: false,
                }}
                item={customerPortal}
                error={!isStripeConnected ? 'Connect Stripe to continue' : ''}
            />
            <BlockNavigation when={!ready} />
            <CustomDataModal
                customData={customData}
                isOpen={showCustomData}
                onClose={handleHideCustomData}
                onSave={handleNewCustomData}
                onDelete={handleDeleteCustomData}
            />
        </div>
    );
}

function AdvancedSettings(props: { values: CustomerPortal }) {
    const company = useContext(CurrentCompanyContext);
    const [verifiedSenders, setVerifiedSenders] = useState<string[]>([]);
    useEffect(() => {
        setVerifiedSenders(company?.verifiedSenders.map((sender) => sender.email) || []);
    }, [company]);

    const [showCustomise, setShowCustomise] = useState(false);
    const [showVerifySender, setShowVerifySender] = useState(false);
    const handleToggleCustomise = () => setShowCustomise(!showCustomise);
    const handleShowVerifySender = () => setShowVerifySender(true);
    const handleHideVerifySender = () => setShowVerifySender(false);
    const handleSenderVerified = (email: string) => setVerifiedSenders([email, ...verifiedSenders]);
    const { values } = props;
    return (
        <>
            <div className="mb-2">
                <Field name="secureMode" type="checkbox">
                    {(props) => (
                        <>
                            <label className="inline-flex items-center">
                                <Toggle {...props.input} />
                                <span className="ml-2 text-sm text-gray-600">Enable secure mode?</span>
                            </label>
                            <p className="text-sm">
                                When secure mode is enabled, you must encrypt your customer's email addresses before
                                sending them to the Customer Portal. Check out the
                                <a
                                    href="https://help.pricewell.com/features/customer_portal/secure-mode-advanced/"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="link ml-2"
                                >
                                    Documentation
                                </a>{' '}
                                for more details.
                            </p>
                        </>
                    )}
                </Field>
            </div>
            <div className="mb-2">
                <Field name="emailAuth" type="checkbox">
                    {(props) => (
                        <>
                            <label className="inline-flex items-center">
                                <Toggle {...props.input} />
                                <span className="ml-2 text-sm text-gray-600">Enable email login?</span>
                            </label>
                            <p className="text-sm">
                                When enabled, the customer will be asked to enter their email address before clicking
                                the "{values.designSettings.buttonText}" button. The customer will recieve an email
                                containing a link which takes them to the Customer Portal.
                            </p>
                        </>
                    )}
                </Field>
            </div>
            <div className={c('mb-2', { hidden: !values.emailAuth })}>
                <button onClick={handleToggleCustomise} className="underline mb-8">
                    {showCustomise ? 'Hide' : 'Show'} Customization Settings
                </button>
                <div className={c({ hidden: !showCustomise })}>
                    <div className="mb-4">
                        <label className="text-sm text-gray-600">Email input placeholder text</label>
                        <Field
                            name="designSettings.placeholderText"
                            type="text"
                            validate={values.emailAuth ? VALIDATORS.required : undefined}
                        >
                            {(props) => (
                                <>
                                    <input {...props.input} className="input w-full" />
                                    {props.meta.error && (
                                        <span className="ml-2">
                                            <Error message={props.meta.error} />
                                        </span>
                                    )}
                                </>
                            )}
                        </Field>
                    </div>
                    <h4 className="mt-2">Messages</h4>
                    <div className="ml-4">
                        <div className="mb-4">
                            <label className="text-sm text-gray-600">Success</label>
                            <Field
                                name="designSettings.messages.checkEmail"
                                type="text"
                                validate={values.emailAuth ? VALIDATORS.required : undefined}
                            >
                                {(props) => (
                                    <>
                                        <input {...props.input} className="input w-full" />
                                        {props.meta.error && (
                                            <span className="ml-2">
                                                <Error message={props.meta.error} />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                        <div className="mb-4">
                            <label className="text-sm text-gray-600">Invalid email</label>
                            <Field
                                name="designSettings.messages.invalidEmail"
                                type="text"
                                validate={values.emailAuth ? VALIDATORS.required : undefined}
                            >
                                {(props) => (
                                    <>
                                        <input {...props.input} className="input w-full" />
                                        {props.meta.error && (
                                            <span className="ml-2">
                                                <Error message={props.meta.error} />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                    </div>
                    <hr />
                    <h4 className="mt-2">Email</h4>
                    <div className="mb-2">
                        <label className="text-sm text-gray-600">From (Name)</label>
                        <Field name="emailAuthTemplate.sender" type="text">
                            {(props) => <input {...props.input} className="input w-full" placeholder="Your Company" />}
                        </Field>
                    </div>
                    <div className="mb-2">
                        <label className="text-sm text-gray-600">
                            From (Email address){' '}
                            <button onClick={handleShowVerifySender} className="link">
                                Verify new sender
                            </button>
                        </label>
                        <Field name="emailAuthTemplate.senderEmail" type="text">
                            {(props) => (
                                <select {...props.input} className="block w-full pl-3 pr-10 select">
                                    <option>Default</option>
                                    {verifiedSenders.map((sender) => (
                                        <option value={sender} key={sender}>
                                            {sender}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </Field>
                        <VerifySenderModal
                            isOpen={showVerifySender}
                            onClose={handleHideVerifySender}
                            onSenderVerified={handleSenderVerified}
                        />
                    </div>
                    <div className="mb-2">
                        <label className="text-sm text-gray-600">Subject</label>
                        <Field name="emailAuthTemplate.subject" type="text">
                            {(props) => (
                                <input {...props.input} className="input w-full" placeholder="subject line..." />
                            )}
                        </Field>
                    </div>
                    <div className="mb-2">
                        <label className="text-sm text-gray-600">Body</label>
                        <Field name="emailAuthTemplate.body" component="textarea" type="text">
                            {(props) => (
                                <textarea {...props.input} className="input w-full" placeholder="email body..." />
                            )}
                        </Field>
                    </div>
                    <div className="text-sm my-2">
                        Edit the email that will be sent to your customers. <br />
                        <span className="font-bold">Note:</span> <pre className="inline">{'{{website}}'}</pre> will be
                        replaced with your website e.g. mysite.com.
                    </div>
                </div>
            </div>
        </>
    );
}
